import { useState, useEffect } from "react";
import { Plus, Trash2, RefreshCw, ExternalLink, Pencil, X, Check } from "lucide-react";
import { statusApi } from "../../api/status";
import { ChipInput, LoadingText, Select } from "../ui";
import { useToast } from "../../hooks/useToast";

interface MonitoredUrl {
  id: string;
  name: string;
  url: string;
  is_active: boolean;
  alert_threshold: number;
  notification_channels: string;
  platform: string;
  created_at: string;
}

interface EditForm {
  name: string;
  url: string;
  alert_threshold: number;
  notification_channels: string;
  platform: string;
}

const PLATFORM_OPTIONS = [
  { value: "phoenix", label: "Phoenix" },
  { value: "kraken",  label: "Kraken" },
];

const PLATFORM_COLOURS: Record<string, string> = {
  phoenix: "bg-orange-50 text-orange-700",
  kraken:  "bg-indigo-50 text-indigo-700",
};

const CHANNEL_OPTIONS = [
  { value: "both",  label: "Email & Slack" },
  { value: "email", label: "Email only" },
  { value: "slack", label: "Slack only" },
];

const CHANNEL_LABELS: Record<string, string> = {
  both: "Email & Slack",
  email: "Email only",
  slack: "Slack only",
};

type Severity = "critical" | "high" | "medium" | "low";
type Routing = Record<Severity, string>;

const ROUTING_OPTIONS = [
  { value: "both", label: "Email & Slack" },
  { value: "slack", label: "Slack only" },
  { value: "email", label: "Email only" },
  { value: "none", label: "Stored in Brian only" },
];

const SEVERITY_ROWS: { key: Severity; label: string; hint: string }[] = [
  { key: "critical", label: "Critical", hint: "Customer-facing or revenue path is down" },
  { key: "high", label: "High", hint: "A real service problem with limited scope" },
  { key: "medium", label: "Medium", hint: "Partial or uncertain impact" },
  { key: "low", label: "Low", hint: "Minor, non-customer-impacting" },
];

function ChannelBadge({ value }: { value: string }) {
  const colours: Record<string, string> = {
    both:  "bg-blue-50 text-blue-700",
    email: "bg-amber-50 text-amber-700",
    slack: "bg-purple-50 text-purple-700",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${colours[value] ?? "bg-gray-100 text-gray-600"}`}>
      {CHANNEL_LABELS[value] ?? value}
    </span>
  );
}

export default function StatusSettings() {
  const toast = useToast();
  const [urls, setUrls] = useState<MonitoredUrl[]>([]);
  const [loading, setLoading] = useState(true);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState("");

  // Alert routing
  const [routing, setRouting] = useState<Routing | null>(null);
  const [incidentChannels, setIncidentChannels] = useState<string[]>([]);
  const [routingSaving, setRoutingSaving] = useState(false);
  const [routingSaved, setRoutingSaved] = useState(false);
  const [routingError, setRoutingError] = useState("");

  // Add form
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [threshold, setThreshold] = useState(1);
  const [channels, setChannels] = useState("both");
  const [platform, setPlatform] = useState("phoenix");
  const [adding, setAdding] = useState(false);
  const [addError, setAddError] = useState("");

  // Inline edit
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ name: "", url: "", alert_threshold: 1, notification_channels: "both", platform: "phoenix" });
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState("");

  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function loadUrls() {
    setLoading(true);
    try {
      const res = await statusApi.listUrls();
      setUrls(res.data);
    } catch {
      setError("Failed to load monitored URLs.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { loadUrls(); }, []);

  useEffect(() => {
    statusApi.getAlertRouting()
      .then((res) => {
        const { slack_channels, ...severities } = res.data as Routing & { slack_channels?: string[] };
        setRouting(severities);
        setIncidentChannels(slack_channels ?? []);
      })
      .catch(() => setRoutingError("Failed to load alert routing."));
  }, []);

  async function saveRouting() {
    if (!routing) return;
    setRoutingSaving(true);
    setRoutingError("");
    try {
      const res = await statusApi.updateAlertRouting({ ...routing, slack_channels: incidentChannels });
      const { slack_channels, ...severities } = res.data as Routing & { slack_channels?: string[] };
      setRouting(severities);
      setIncidentChannels(slack_channels ?? []);
      setRoutingSaved(true);
      setTimeout(() => setRoutingSaved(false), 3000);
      toast.success("Alert routing saved");
    } catch { return; } finally {
      setRoutingSaving(false);
    }
  }

  async function handleAdd(e: React.FormEvent) {
    e.preventDefault();
    setAddError("");
    const trimName = name.trim();
    const trimUrl = url.trim();
    if (!trimName || !trimUrl) { setAddError("Both name and URL are required."); return; }
    if (!/^https?:\/\//.test(trimUrl)) { setAddError("URL must start with http:// or https://"); return; }
    if (threshold < 1) { setAddError("Alert threshold must be at least 1."); return; }
    setAdding(true);
    try {
      const res = await statusApi.addUrl(trimName, trimUrl, threshold, channels, platform);
      setUrls((prev) => [...prev, res.data]);
      setName(""); setUrl(""); setThreshold(1); setChannels("both"); setPlatform("phoenix");
    } catch { return; } finally {
      setAdding(false);
    }
  }

  function startEdit(u: MonitoredUrl) {
    setEditingId(u.id);
    setEditForm({
      name: u.name,
      url: u.url,
      alert_threshold: u.alert_threshold ?? 1,
      notification_channels: u.notification_channels ?? "both",
      platform: u.platform ?? "phoenix",
    });
    setEditError("");
  }

  function cancelEdit() {
    setEditingId(null);
    setEditError("");
  }

  async function handleSave(id: string) {
    setEditError("");
    const trimName = editForm.name.trim();
    const trimUrl = editForm.url.trim();
    if (!trimName || !trimUrl) { setEditError("Name and URL are required."); return; }
    if (!/^https?:\/\//.test(trimUrl)) { setEditError("URL must start with http:// or https://"); return; }
    if (editForm.alert_threshold < 1) { setEditError("Alert threshold must be at least 1."); return; }
    setSaving(true);
    try {
      const res = await statusApi.updateUrl(id, {
        name: trimName,
        url: trimUrl,
        alert_threshold: editForm.alert_threshold,
        notification_channels: editForm.notification_channels,
        platform: editForm.platform,
      });
      setUrls((prev) => prev.map((u) => u.id === id ? res.data : u));
      setEditingId(null);
    } catch { return; } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id: string) {
    setDeletingId(id);
    try {
      await statusApi.deleteUrl(id);
      setUrls((prev) => prev.filter((u) => u.id !== id));
    } catch { return; } finally {
      setDeletingId(null);
    }
  }

  async function handleCheckNow() {
    setChecking(true);
    try {
      await statusApi.checkNow();
    } catch { return; } finally {
      setChecking(false);
    }
  }

  return (
    <div>
      <section className="mb-10">
        <h2 className="text-base font-semibold text-gray-900">Alert routing</h2>
        <p className="mt-1 text-sm text-gray-500">
          Choose where each severity is delivered when an incident opens or resolves. "Stored in Brian only" records the incident without notifying any channel. Changes apply immediately — no redeploy needed.
        </p>
        <div className="mt-4 rounded-xl border border-gray-200 divide-y divide-gray-100">
          {SEVERITY_ROWS.map((row) => (
            <div key={row.key} className="flex items-center justify-between gap-4 px-4 py-3">
              <div className="min-w-0">
                <p className="text-sm font-medium capitalize text-gray-800">{row.label}</p>
                <p className="text-xs text-gray-400">{row.hint}</p>
              </div>
              <Select
                value={routing?.[row.key] ?? "none"}
                onChange={(e) => setRouting((r) => (r ? { ...r, [row.key]: e.target.value } : r))}
                disabled={!routing}
                className="w-48 shrink-0"
              >
                {ROUTING_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl border border-gray-200 p-4">
          <label className="block text-sm font-medium text-gray-800">Slack channels for incidents</label>
          <p className="mb-2 mt-0.5 text-xs text-gray-400">
            Which Slack channels incident alerts post to. Brian must be invited to each channel. Leave empty to use the default alert channels.
          </p>
          <ChipInput
            values={incidentChannels}
            onChange={setIncidentChannels}
            placeholder="Add a channel ID (e.g. C0123456789) and press Enter"
            disabled={!routing}
          />
        </div>
        <div className="mt-3 flex items-center gap-3">
          <button
            onClick={saveRouting}
            disabled={routingSaving || !routing}
            className="flex items-center gap-1.5 rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50"
          >
            <Check size={14} />
            {routingSaving ? "Saving…" : routingSaved ? "Saved!" : "Save routing"}
          </button>
          {routingError && <span className="text-sm text-red-600">{routingError}</span>}
        </div>
      </section>

      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Service status monitoring</h2>
          <p className="mt-1 text-sm text-gray-500">
            Monitor any URL — health endpoints, homepages, APIs. A GET request is made every 5 minutes: 200 under 2 s is healthy, 200 over 2 s is degraded, anything else is down. Assign each to a platform so it appears in the correct monitoring view.
          </p>
        </div>
        <button
          onClick={handleCheckNow}
          disabled={checking}
          className="flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-gray-300 hover:text-gray-800 disabled:opacity-50"
        >
          <RefreshCw size={12} className={checking ? "animate-spin" : ""} />
          Check now
        </button>
      </div>

      {error && <p className="mb-4 text-sm text-red-600">{error}</p>}

      {/* Existing URLs */}
      {loading ? (
        <LoadingText />
      ) : urls.length === 0 ? (
        <div className="mb-6 rounded-xl border border-dashed border-gray-200 px-5 py-6 text-center">
          <p className="text-sm text-gray-400">No URLs configured yet. Add one below.</p>
        </div>
      ) : (
        <div className="mb-6 rounded-xl border border-gray-200 divide-y divide-gray-100">
          {urls.map((u) =>
            editingId === u.id ? (
              /* ── Inline edit row ── */
              <div key={u.id} className="px-4 py-3 space-y-3">
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Display name</label>
                    <input
                      type="text"
                      value={editForm.name}
                      onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                      maxLength={255}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">URL</label>
                    <input
                      type="url"
                      value={editForm.url}
                      onChange={(e) => setEditForm((f) => ({ ...f, url: e.target.value }))}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">
                      Alert after N consecutive failures
                    </label>
                    <input
                      type="number"
                      min={1}
                      max={20}
                      value={editForm.alert_threshold}
                      onChange={(e) => setEditForm((f) => ({ ...f, alert_threshold: parseInt(e.target.value, 10) || 1 }))}
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Notify via</label>
                    <Select
                      value={editForm.notification_channels}
                      onChange={(e) => setEditForm((f) => ({ ...f, notification_channels: e.target.value }))}
                      className="w-full"
                    >
                      {CHANNEL_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Select>
                  </div>
                  <div>
                    <label className="mb-1 block text-xs font-medium text-gray-600">Platform</label>
                    <Select
                      value={editForm.platform}
                      onChange={(e) => setEditForm((f) => ({ ...f, platform: e.target.value }))}
                      className="w-full"
                    >
                      {PLATFORM_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>{o.label}</option>
                      ))}
                    </Select>
                  </div>
                </div>
                {editError && <p className="text-xs text-red-600">{editError}</p>}
                <div className="flex items-center gap-2 justify-end">
                  <button
                    onClick={cancelEdit}
                    className="flex items-center gap-1 rounded-lg border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-600 hover:text-gray-800"
                  >
                    <X size={12} /> Cancel
                  </button>
                  <button
                    onClick={() => handleSave(u.id)}
                    disabled={saving}
                    className="flex items-center gap-1 rounded-lg bg-[#1D345F] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#162847] disabled:opacity-50"
                  >
                    <Check size={12} /> {saving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            ) : (
              /* ── Read row ── */
              <div key={u.id} className="flex items-center gap-3 px-4 py-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{u.name}</p>
                  <a
                    href={u.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 truncate text-xs text-gray-400 hover:text-blue-600"
                  >
                    {u.url}
                    <ExternalLink size={10} className="shrink-0" />
                  </a>
                  <div className="mt-1.5 flex items-center gap-2 flex-wrap">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${PLATFORM_COLOURS[u.platform] ?? "bg-gray-100 text-gray-600"}`}>
                      {u.platform ?? "phoenix"}
                    </span>
                    <ChannelBadge value={u.notification_channels ?? "both"} />
                    <span className="text-xs text-gray-400">
                      Alert after {u.alert_threshold ?? 1} consecutive {(u.alert_threshold ?? 1) === 1 ? "failure" : "failures"}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => startEdit(u)}
                  className="rounded p-1.5 text-gray-400 hover:text-blue-600"
                  title="Edit"
                >
                  <Pencil size={14} />
                </button>
                <button
                  onClick={() => handleDelete(u.id)}
                  disabled={deletingId === u.id}
                  className="rounded p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-40"
                  title="Remove"
                >
                  <Trash2 size={15} />
                </button>
              </div>
            )
          )}
        </div>
      )}

      {/* Add URL form */}
      <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
        <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">Add endpoint</p>
        <form onSubmit={handleAdd} className="space-y-3">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Display name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Main API"
                maxLength={255}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">URL</label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://example.com or https://api.example.com/health"
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Alert after N consecutive failures
              </label>
              <input
                type="number"
                min={1}
                max={20}
                value={threshold}
                onChange={(e) => setThreshold(parseInt(e.target.value, 10) || 1)}
                className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Notify via</label>
              <Select
                value={channels}
                onChange={(e) => setChannels(e.target.value)}
                className="w-full"
              >
                {CHANNEL_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">Platform</label>
              <Select
                value={platform}
                onChange={(e) => setPlatform(e.target.value)}
                className="w-full"
              >
                {PLATFORM_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Select>
            </div>
          </div>
          {addError && <p className="text-xs text-red-600">{addError}</p>}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={adding}
              className="flex items-center gap-1.5 rounded-lg bg-[#1D345F] px-4 py-2 text-sm font-medium text-white hover:bg-[#162847] disabled:opacity-50"
            >
              <Plus size={14} />
              {adding ? "Adding…" : "Add endpoint"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
